import React, { memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import _ from 'lodash';
import PropTypes from 'prop-types';

import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import {
  loadRunCitationsAction,
  selectCitationsByRun,
  selectFormByExecution,
  selectRunCitationsStatus,
  selectStepsByRun,
} from 'store/aiProcessRuns';

import { AiActivityCitationsContext } from '../AiActivityCitations.context';
import { buildCitationRefreshSignature } from '../AiActivityCitations.utils';

// LEN-213: loads the run-wide citation list once per run drawer and re-fetches only when
// a new citation can have appeared (see buildCitationRefreshSignature).
const AiActivityCitationsProvider = ({ executionId = null, children }) => {
  const dispatch = useDispatch();
  const citations = useSelector(selectCitationsByRun(executionId));
  const status = useSelector(selectRunCitationsStatus(executionId));
  const form = useSelector(selectFormByExecution(executionId));
  const steps = useSelector(selectStepsByRun(executionId));

  const refreshSignature = useMemo(
    () => buildCitationRefreshSignature({ form, steps }),
    [form, steps],
  );

  const contextValue = useMemo(() => ({ citations, status }), [citations, status]);

  useEffectSafeStrictMode(() => {
    if (!_.isNil(executionId)) {
      dispatch(loadRunCitationsAction({ executionId, force: true }));
    }
  }, [dispatch, executionId, refreshSignature]);

  return (
    <AiActivityCitationsContext.Provider value={contextValue}>
      {children}
    </AiActivityCitationsContext.Provider>
  );
};

AiActivityCitationsProvider.propTypes = {
  children: PropTypes.node.isRequired,
  executionId: PropTypes.string,
};

export default memo(AiActivityCitationsProvider);
